import { useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Baker-lite — the prompt → app-scaffold on-ramp.
 *
 * Founder ruling 2026-08-11 (verbatim):
 *   "Is Baker-lite like getting the initial app to build-stage with a prompt,
 *   and then Baker is the builder itself? I like that a lot if so."
 *
 * The visitor describes an app idea in plain English. Claude drafts a spec.
 * The spec becomes the on-ramp into full Baker (closed alpha).
 */
export default function BakerLitePage() {
  const [idea, setIdea] = useState("");
  const [spec, setSpec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (idea.trim().length < 20) {
      setError("Tell Baker-lite a bit more — at least 20 characters.");
      return;
    }
    setBusy(true);
    setError("");
    setSpec(null);
    try {
      const r = await fetch(`${API}/api/sage/baker-lite/scaffold`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idea }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Baker-lite failed to draft.");
      setSpec(d.spec);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative" data-testid="baker-lite-page">
      <TopNav />
      <div className="mx-auto max-w-4xl px-4 md:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">The prompt-to-scaffold on-ramp</div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase mb-4">
          Baker-lite
        </h1>
        <p className="text-lg md:text-xl text-sage-muted mb-8 leading-relaxed sage-prose max-w-3xl">
          Describe an app in plain English. Baker-lite drafts the spec.
          Baker (the full builder) turns the spec into runnable starter code.
        </p>

        {/* Free ask-box · same shape as Products + Free-Wall (Founder ruling 2026-08-11) */}
        <BakerLiteAskBox />

        {/* Tutorial · what Baker-lite is vs Baker */}
        <div className="grid md:grid-cols-2 gap-4 mb-10" data-testid="tutorial">
          <div className="border-2 border-sage-accent p-5 bg-sage-pale/30">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">Baker-lite · you are here</div>
            <div className="text-2xl font-bold uppercase text-sage-text mb-2">Idea → Spec</div>
            <div className="text-sm text-sage-muted sage-prose">
              Take an idea from "napkin sketch" to "build-ready spec." No code yet. Just the shape: data models, routes, pages, integrations, honest bake estimate.
            </div>
          </div>
          <div className="border border-sage-border p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2">Baker · workshop</div>
            <div className="text-2xl font-bold uppercase text-sage-text mb-2">Spec → Code</div>
            <div className="text-sm text-sage-muted sage-prose">
              The workshop. Takes a Baker-lite spec, generates a runnable starter (FastAPI + React + Mongo + README). Master-only door · every hand-off recorded on the build log.
            </div>
          </div>
        </div>

        {/* Idea input */}
        <div className="mb-6" data-testid="idea-input">
          <label className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-2 block">
            Describe your app in plain English
          </label>
          <textarea
            data-testid="idea-textarea"
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
            placeholder="Example: An app where writers can log their daily word counts, get streak tracking, and share progress with a private group of friends."
            rows={6}
            maxLength={2000}
            className="w-full border border-sage-border bg-sage-bg px-4 py-3 text-sage-text focus:border-sage-accent outline-none sage-prose"
          />
          <div className="text-xs text-sage-faint mt-1 text-right">{idea.length}/2000</div>
        </div>

        {error && <div className="text-sm text-sage-warn mb-4" data-testid="baker-lite-error">{error}</div>}

        <button
          onClick={submit}
          disabled={busy}
          data-testid="baker-lite-submit"
          className="w-full md:w-auto border-2 border-sage-accent bg-sage-accent text-sage-bg px-8 py-3 text-sm uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-10"
        >
          {busy ? "Baker-lite is drafting…" : "Draft the spec"}
        </button>

        {/* Spec output */}
        {spec && <SpecView spec={spec} />}
      </div>
    </div>
  );
}

function SpecView({ spec }) {
  return (
    <div className="border-2 border-sage-accent p-6" data-testid="spec-view">
      <div className="text-xs uppercase tracking-[0.2em] text-sage-accent mb-2">Draft spec · ready for Baker</div>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-sage-text mb-2">{spec.name || "Untitled"}</h2>
      {spec.tagline && <p className="text-lg text-sage-muted italic mb-4">{spec.tagline}</p>}
      {spec.pitch && <p className="text-base text-sage-text sage-prose mb-6">{spec.pitch}</p>}

      {spec.estimated_bake_hours && (
        <div className="inline-block border border-sage-accent text-sage-accent px-3 py-1 text-xs uppercase tracking-wider mb-6" data-testid="bake-estimate">
          Estimated bake: {spec.estimated_bake_hours} hour{spec.estimated_bake_hours === 1 ? "" : "s"}
        </div>
      )}

      {Array.isArray(spec.data_models) && spec.data_models.length > 0 && (
        <Section title="Data models">
          {spec.data_models.map((m, i) => (
            <div key={i} className="mb-3">
              <div className="text-sm font-bold text-sage-text">{m.name}</div>
              <ul className="text-xs text-sage-muted mt-1 ml-4 space-y-1">
                {(m.fields || []).map((f, j) => <li key={j}>· {f}</li>)}
              </ul>
            </div>
          ))}
        </Section>
      )}

      {Array.isArray(spec.backend_routes) && spec.backend_routes.length > 0 && (
        <Section title="Backend routes">
          <ul className="text-sm text-sage-muted space-y-1">
            {spec.backend_routes.map((r, i) => (
              <li key={i}>
                <code className="text-sage-accent text-xs">{r.method} {r.path}</code>
                <span className="text-sage-muted ml-2">— {r.purpose}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(spec.frontend_pages) && spec.frontend_pages.length > 0 && (
        <Section title="Frontend pages">
          <ul className="text-sm text-sage-muted space-y-1">
            {spec.frontend_pages.map((p, i) => (
              <li key={i}>
                <code className="text-sage-accent text-xs">{p.path}</code>
                <span className="text-sage-muted ml-2">— {p.purpose}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(spec.integrations) && spec.integrations.length > 0 && (
        <Section title="Integrations">
          <ul className="text-sm text-sage-muted space-y-1">
            {spec.integrations.map((it, i) => <li key={i}>· {it}</li>)}
          </ul>
        </Section>
      )}

      {Array.isArray(spec.honesty_flags) && spec.honesty_flags.length > 0 && (
        <Section title="Honesty flags · read these" warn>
          <ul className="text-sm text-sage-warn sage-prose space-y-1">
            {spec.honesty_flags.map((h, i) => <li key={i}>⚑ {h}</li>)}
          </ul>
        </Section>
      )}

      <div className="mt-8 pt-6 border-t border-sage-border">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Next</div>
        <p className="text-sm text-sage-muted sage-prose mb-3">
          Baker-lite drafted the shape. Baker turns it into code. Hand off through the master workshop.
        </p>
        <Link
          to="/baker/workshop"
          className="inline-block border border-sage-accent text-sage-accent px-4 py-2 text-xs uppercase tracking-wider hover:bg-sage-accent hover:text-sage-bg transition-colors"
          data-testid="handoff-to-baker"
        >
          Hand off to Baker →
        </Link>
      </div>
    </div>
  );
}

function BakerLiteAskBox() {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cached, setCached] = useState(false);

  const ask = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    setAnswer(null);
    try {
      const r = await fetch(`${API}/api/sage/faq/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q.trim() }),
      });
      const d = await r.json();
      if (r.ok) {
        setAnswer(d.answer);
        setCached(!!d.cached);
      } else {
        setAnswer("Sage couldn't answer that one. Try rephrasing?");
      }
    } catch {
      setAnswer("Network hiccup. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") ask(); };

  return (
    <div className="mb-10 border border-sage-accent/40 bg-sage-pale/20 p-5" data-testid="baker-lite-faq-ask-box">
      <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">
        Ask Sage about Baker or Baker-lite · free · anonymous · cache-first
      </div>
      <div className="flex gap-2">
        <input
          data-testid="baker-lite-faq-input"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="How much of a real app does Baker actually build? · Is my spec kept private? · Can I edit before hand-off?"
          maxLength={500}
          className="flex-1 border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
        />
        <button
          onClick={ask}
          disabled={busy || q.trim().length < 3}
          data-testid="baker-lite-faq-submit"
          className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
        >
          {busy ? "…" : "Ask"}
        </button>
      </div>
      {answer && (
        <div className="mt-4 border-t border-sage-border pt-4" data-testid="baker-lite-faq-answer">
          <div className="flex justify-between items-center mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted">Sage says</div>
            {cached && (
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent" data-testid="baker-lite-faq-cached-badge">
                Served from cache · 0 tokens burned
              </div>
            )}
          </div>
          <div className="text-sm text-sage-text sage-prose whitespace-pre-wrap">{answer}</div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children, warn }) {
  return (
    <div className={`mb-6 pb-4 border-b border-sage-border ${warn ? "border-sage-warn/40" : ""}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] mb-2 ${warn ? "text-sage-warn" : "text-sage-muted"}`}>{title}</div>
      {children}
    </div>
  );
}
